import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Home, Compass, Rocket, Users, Mail, Folder, ArrowRight, MoveLeft, Search } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ART } from "@/lib/artwork";
import { SEO } from "@/components/SEO";

/**
 * NotFound — friendly 404 that surfaces the most useful destinations
 * for whoever wandered off. Public visitors get the marketing routes;
 * signed-in users additionally see their dashboard, data rooms, and
 * (if admin) the admin panel.
 */
const PUBLIC_LINKS = [
  { to: "/", icon: Home, key: "home", en: "Home", ar: "الرئيسية" },
  { to: "/journey?type=retail-storefront", icon: Compass, key: "journey", en: "Start your journey", ar: "ابدأ رحلتك" },
  { to: "/contact", icon: Mail, key: "contact", en: "Contact us", ar: "تواصل معنا" },
];

const USER_LINKS = [
  { to: "/dashboard", icon: Rocket, key: "dashboard", en: "Dashboard", ar: "لوحة التحكم" },
  { to: "/data-rooms", icon: Folder, key: "rooms", en: "Data rooms", ar: "غرف البيانات" },
];

const ADMIN_LINKS = [
  { to: "/admin", icon: Rocket, key: "admin", en: "Admin panel", ar: "لوحة الإدارة" },
];

export default function NotFound() {
  const { i18n } = useTranslation();
  const { user } = useAuth();
  const location = useLocation();
  const isAr = i18n.language === "ar";

  const links = [
    ...PUBLIC_LINKS,
    ...(user ? USER_LINKS : []),
    ...(user?.role === "admin" ? ADMIN_LINKS : []),
  ];

  return (
    <section className="relative overflow-hidden mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-16 sm:py-24" data-testid="not-found-page">
      <SEO title="Page not found" description="The page you were looking for is not available." noindex />
      <div className="absolute inset-0 -z-10 overflow-hidden" aria-hidden>
        <img src={ART.soft} alt="" className="h-full w-full object-cover opacity-45" />
        <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-background/80 to-background" />
      </div>
      <motion.div
        aria-hidden
        className="pointer-events-none absolute -top-24 -left-16 h-64 w-64 rounded-full bg-primary/20 blur-3xl"
        animate={{ x: [0, 20, -8, 0], y: [0, 15, -5, 0] }}
        transition={{ duration: 22, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        aria-hidden
        className="pointer-events-none absolute -bottom-24 -right-16 h-64 w-64 rounded-full bg-secondary/25 blur-3xl"
        animate={{ x: [0, -18, 6, 0], y: [0, -12, 5, 0] }}
        transition={{ duration: 26, repeat: Infinity, ease: "easeInOut" }}
      />

      <div className="relative text-center">
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 px-4 py-1.5 text-xs uppercase tracking-widest text-primary font-semibold"
        >
          <Search className="h-3.5 w-3.5" />
          {isAr ? "الصفحة غير موجودة" : "Page not found"}
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, delay: 0.06 }}
          className="font-display text-5xl sm:text-6xl lg:text-7xl font-semibold tracking-tight mt-5"
        >
          404
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, delay: 0.12 }}
          className="text-sm sm:text-base text-muted-foreground mt-3 max-w-md mx-auto"
        >
          {(() => {
            // Redirected 404s (e.g. an archived /p/<slug>) pass the original
            // path in router state — otherwise we'd always print "/404".
            const requested = location.state?.from || location.pathname;
            return isAr
              ? `لم نتمكن من العثور على "${requested}". لعلك تجد ما تبحث عنه ضمن الوجهات أدناه.`
              : `We couldn't find "${requested}". Try one of these destinations instead.`;
          })()}
        </motion.p>

        <motion.div
          className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
          initial="hidden"
          animate="show"
          variants={{ show: { transition: { staggerChildren: 0.06, delayChildren: 0.18 } } }}
        >
          {links.map((l) => {
            const Icon = l.icon;
            return (
              <motion.div
                key={l.key}
                variants={{ hidden: { opacity: 0, y: 10 }, show: { opacity: 1, y: 0 } }}
              >
                <Link
                  to={l.to}
                  className="group relative flex items-center gap-3 rounded-2xl border border-border bg-background/70 backdrop-blur p-4 hover:border-primary/40 hover:shadow-[0_8px_24px_rgba(45,84,94,0.10)] transition-[border-color,box-shadow,transform] hover:-translate-y-0.5"
                  data-testid={`not-found-link-${l.key}`}
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary group-hover:bg-primary/15 transition-colors">
                    <Icon className="h-5 w-5" />
                  </span>
                  <span className="min-w-0 flex-1 text-start">
                    <p className="text-sm font-semibold">{isAr ? l.ar : l.en}</p>
                  </span>
                  <ArrowRight className={`h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-0.5 ${isAr ? "rotate-180 group-hover:-translate-x-0.5" : ""}`} />
                </Link>
              </motion.div>
            );
          })}
        </motion.div>

        <motion.div
          className="mt-8"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.4 }}
        >
          <button
            type="button"
            onClick={() => window.history.length > 1 ? window.history.back() : null}
            className="inline-flex items-center gap-2 text-xs sm:text-sm text-muted-foreground hover:text-foreground transition-colors"
            data-testid="not-found-back"
          >
            <MoveLeft className={`h-4 w-4 ${isAr ? "rotate-180" : ""}`} />
            {isAr ? "الرجوع للصفحة السابقة" : "Go back to the previous page"}
          </button>
        </motion.div>
      </div>
    </section>
  );
}
